/**
 * StaticDrawingRenderer - Static rendering of drawing paths
 *
 * Handles static rendering with modifier support (pre-snap markers).
 * Pure visual component - no state or handlers.
 */

import { useMemo } from 'react'
import type { Drawing } from '@/types/Drawing.types'
import type { BlockModifier, PathSplitModifier } from '@/components/blocks/modifiers/Types'
import { useBlockRenderContext } from '@/components/blocks/BlockRenderContext'
import { SplitPathRenderer } from '@/components/blocks/modifiers/SplitPathRenderer'
import { buildSVGPath } from '@/utils/SvgPath.utils'
import { useTheme } from '@/contexts/SettingsContext'
import { getThemeAwareColor } from '@/utils/ColorUtils'
import { FilledLineEnding } from '@/components/canvas/FilledLineEnding'
import {
	DASH_PATTERN_LENGTH_MULTIPLIER,
	DASH_PATTERN_GAP_MULTIPLIER,
	LINE_END_NONE,
} from '@/constants'

interface StaticDrawingRendererProps {
	drawing: Drawing
	modifiers: BlockModifier[]
}

/**
 * Renders static drawing paths with modifier support
 *
 * @param drawing - The resolved drawing data
 * @param modifiers - Block modifiers (pre-snap markers, etc.)
 *
 * Checks for path split modifiers and renders accordingly.
 * Uses scale factors and coordinate transform from BlockRenderContext.
 */
export function StaticDrawingRenderer({
	drawing,
	modifiers,
}: StaticDrawingRendererProps) {
	const { theme } = useTheme()
	const { scale, coordinateTransform, coordSystem } = useBlockRenderContext()

	// Build SVG path from drawing data with coordinate transform (Y-flip)
	const { d, endDirection } = useMemo(() => {
		return buildSVGPath(drawing, coordinateTransform)
	}, [drawing, coordinateTransform])

	// Check for path split modifier (pre-snap)
	const pathSplitMod = modifiers.find((m) => m.type === 'path_split') as PathSplitModifier | undefined

	// If path split modifier exists and we have coordinate system, use SplitPathRenderer
	if (pathSplitMod && coordSystem) {
		return (
			<g>
				<SplitPathRenderer drawing={drawing} modifier={pathSplitMod} coordSystem={coordSystem} />
			</g>
		)
	}

	if (!d) return null

	// Extract style properties
	const { color, strokeWidth, lineStyle, lineEnd } = drawing.style
	const displayColor = getThemeAwareColor(color, theme)

	// Apply scale factors
	const scaledStrokeWidth = strokeWidth * (scale?.stroke ?? 1.0)
	const finalStrokeWidth = Math.max(
		scaledStrokeWidth,
		scale?.minStroke ?? 1
	)

	// Calculate dash pattern if dashed
	const dashArray =
		lineStyle === 'dashed'
			? [
					finalStrokeWidth * DASH_PATTERN_LENGTH_MULTIPLIER,
					finalStrokeWidth * DASH_PATTERN_GAP_MULTIPLIER,
			  ].join(' ')
			: undefined

	const hasEnding = lineEnd !== LINE_END_NONE && endDirection

	return (
		<g style={{ pointerEvents: 'none' }}>
			{/* Main path */}
			<path
				d={d}
				fill="none"
				stroke={displayColor}
				strokeWidth={finalStrokeWidth}
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeDasharray={dashArray}
			/>

			{/* Filled line ending */}
			{hasEnding && (
				<FilledLineEnding
					lineEnd={lineEnd as 'arrow' | 'tShape'}
					point={endDirection!.point}
					angle={endDirection!.angle}
					strokeWidth={finalStrokeWidth}
					color={displayColor}
				/>
			)}
		</g>
	)
}
